import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Video, Search, Home, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DemoVideoCard } from '@/components/video/DemoVideoCard';
import { DemoVideoModal } from '@/components/video/DemoVideoModal';
import { DEMO_VIDEOS, VIDEO_CATEGORIES, type DemoVideo } from '@/constants/demoVideos';
import { useAuth } from '@/contexts/AuthContext';

export default function DemoVideosPage() {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedVideo, setSelectedVideo] = useState<DemoVideo | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const filteredVideos = useMemo(() => {
    return DEMO_VIDEOS.filter((video) => {
      const matchesSearch =
        video.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        video.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || video.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory]);

  const featuredVideos = filteredVideos.filter((v) => v.isFeatured);
  const regularVideos = filteredVideos.filter((v) => !v.isFeatured);

  const handlePlayVideo = (video: DemoVideo) => {
    setSelectedVideo(video);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedVideo(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      {/* Header */}
      <div className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-16 items-center justify-between px-4">
          <Button
            variant="outline"
            size="sm"
            className="rounded-full"
            onClick={() => window.history.back()}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          <div className="flex items-center gap-2">
            <Video className="h-6 w-6 text-primary" />
            <h1 className="text-xl font-bold">Demo Videos</h1>
          </div>

          <Link to={user ? '/app' : '/'}>
            <Button variant="outline" size="sm" className="rounded-full">
              <Home className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Home</span>
            </Button>
          </Link>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Hero */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold mb-2">Learn How to Use VoiceThera</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Watch our demo videos to get the most out of your VoiceThera experience.
            From getting started to advanced features, we've got you covered.
          </p>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search videos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
            <TabsList>
              {VIDEO_CATEGORIES.map((category) => (
                <TabsTrigger key={category.id} value={category.id}>
                  {category.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {/* Featured Videos */}
        {featuredVideos.length > 0 && (
          <section className="mb-10">
            <h3 className="text-lg font-semibold mb-4">Featured Videos</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {featuredVideos.map((video) => (
                <DemoVideoCard key={video.id} video={video} onPlay={handlePlayVideo} />
              ))}
            </div>
          </section>
        )}

        {/* All Videos */}
        {regularVideos.length > 0 && (
          <section>
            <h3 className="text-lg font-semibold mb-4">All Videos</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {regularVideos.map((video) => (
                <DemoVideoCard key={video.id} video={video} onPlay={handlePlayVideo} />
              ))}
            </div>
          </section>
        )}

        {/* Empty State */}
        {filteredVideos.length === 0 && (
          <div className="text-center py-16">
            <Video className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-lg font-semibold mb-2">No videos found</h3>
            <p className="text-muted-foreground">
              Try adjusting your search or filter to find what you're looking for.
            </p>
          </div>
        )}

        {/* Help CTA */}
        <section className="mt-12 text-center py-8 bg-muted/30 rounded-lg">
          <h3 className="text-lg font-semibold mb-2">Need more help?</h3>
          <p className="text-muted-foreground mb-4">
            Visit our Help Center for detailed guides and FAQs.
          </p>
          <Link to="/help">
            <Button>Go to Help Center</Button>
          </Link>
        </section>
      </div>

      {/* Video Modal */}
      <DemoVideoModal video={selectedVideo} open={modalOpen} onClose={handleCloseModal} />
    </div>
  );
}
